import type { HealthCheck, HealthChecks, VendorHealth } from "@dynamic-rag/contracts";
import {
  Brain,
  Cpu,
  Database,
  LibraryBig,
  PlugZap,
  RefreshCw,
  Server,
  Settings2,
  Sparkles,
  Ticket,
  Workflow,
  X,
} from "lucide-react";
import { useEffect, useId, useRef, type ReactNode } from "react";

import { errorMessage } from "../../api/client";
import { useHealth, useVendorHealth } from "../../api/queries";
import { formatMs } from "../../lib/format";
import { Button } from "../../ui/Button";
import { Alert, Skeleton, StatusPill, type PillTone } from "../../ui/Primitives";
import styles from "./StatusDrawer.module.css";

// Circuit breakers on outside services (RL-03); "paused" after repeated failures.
const CIRCUIT_NAMES: Record<string, string> = {
  jev: "JEV",
  llm: "Language model",
  knowledge: "Knowledge source",
  ticketing: "Ticketing",
  support_queue: "Support queue",
};

const CHECKS: Record<keyof HealthChecks, { label: string; icon: ReactNode }> = {
  mongodb: { label: "MongoDB", icon: <Database size={16} /> },
  configuration: { label: "Configuration", icon: <Settings2 size={16} /> },
  knowledge_source: { label: "Knowledge source", icon: <LibraryBig size={16} /> },
  chromadb: { label: "ChromaDB", icon: <Server size={16} /> },
  worker: { label: "Ingestion worker", icon: <Workflow size={16} /> },
  embedding_model: { label: "Embedding model", icon: <Cpu size={16} /> },
};

const CONNECTIONS: { key: keyof Omit<VendorHealth, "checked_at">; label: string; icon: ReactNode }[] = [
  { key: "llm", label: "Language model", icon: <Sparkles size={16} /> },
  { key: "jev", label: "JEV", icon: <Brain size={16} /> },
  { key: "knowledge_source", label: "Knowledge source", icon: <LibraryBig size={16} /> },
  { key: "ticketing", label: "Ticketing", icon: <Ticket size={16} /> },
];

function checkLine(check: HealthCheck): string {
  const latency = check.latency_ms !== null && check.latency_ms !== undefined ? formatMs(check.latency_ms) : null;
  return [check.detail, latency].filter(Boolean).join(" · ");
}

function toneOf(check: HealthCheck): PillTone {
  return check.status === "ok"
    ? "success"
    : check.status === "degraded"
      ? "warning"
      : check.status === "down"
        ? "danger"
        : "neutral";
}

function CheckRow({ icon, label, check }: { icon: ReactNode; label: string; check: HealthCheck }) {
  return (
    <li className={styles.check}>
      <span className={styles.checkIcon} aria-hidden="true">
        {icon}
      </span>
      <span className={styles.checkText}>
        <span className={styles.checkName}>{label}</span>
        <span className={styles.checkDetail}>{checkLine(check)}</span>
      </span>
      <StatusPill tone={toneOf(check)}>{check.status === "off" ? "not used" : check.status}</StatusPill>
    </li>
  );
}

/**
 * System status, in a side panel opened from the status pill in the bar (moved off the
 * Dashboard on 22 Oct 2026). The services behind every answer are checked every 30 seconds
 * without paid calls; "Test connections" makes a live call to the language model, JEV, the
 * knowledge source and Jira with the keys from the server's .env, which shows a revoked or
 * expired key. Built on <dialog>: focus stays inside while it is open, and Escape closes it.
 */
export function StatusDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const health = useHealth();
  const vendors = useVendorHealth();

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal?.();
    if (!open && dialog.open) dialog.close?.();
  }, [open]);

  return (
    <dialog
      ref={ref}
      className={styles.drawer}
      aria-labelledby={titleId}
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
      onClick={(event) => {
        if (event.target === ref.current) onClose();
      }}
    >
      {open ? (
        <div className={styles.panel}>
          <header className={styles.head}>
            <div className={styles.titles}>
              <h2 id={titleId} className={styles.title}>
                System status
              </h2>
              <p className={styles.sub}>
                Checked every 30 seconds
                {health.data ? ` · API ${health.data.version} · ${health.data.vendor_mode} vendors` : ""}
              </p>
            </div>
            <button type="button" className={styles.close} onClick={onClose} aria-label="Close system status">
              <X size={18} />
            </button>
          </header>

          <section className={styles.block} aria-label="Services">
            <div className={styles.blockHead}>
              <h3 className={styles.blockTitle}>Services</h3>
              <Button
                size="sm"
                icon={<RefreshCw size={14} />}
                onClick={() => void health.refetch()}
                loading={health.isFetching}
              >
                Check now
              </Button>
            </div>
            {health.data?.circuits?.length ? (
              <div className={styles.circuits} aria-label="Circuit breakers">
                {health.data.circuits.map((circuit) => (
                  <StatusPill
                    key={circuit.name}
                    tone={circuit.state === "closed" ? "success" : circuit.state === "open" ? "danger" : "warning"}
                  >
                    {CIRCUIT_NAMES[circuit.name] ?? circuit.name}:{" "}
                    {circuit.state === "open"
                      ? `paused${circuit.retry_after_s ? `, retrying in ${Math.ceil(circuit.retry_after_s)} s` : ""}`
                      : circuit.state === "half_open"
                        ? "testing"
                        : "working"}
                  </StatusPill>
                ))}
              </div>
            ) : null}
            {health.data ? (
              <ul className={styles.checks} role="list" aria-label="Services">
                {(Object.keys(CHECKS) as (keyof HealthChecks)[]).map((key) => (
                  <CheckRow key={key} icon={CHECKS[key].icon} label={CHECKS[key].label} check={health.data.checks[key]} />
                ))}
              </ul>
            ) : health.isError ? (
              <Alert tone="danger">{errorMessage(health.error)}</Alert>
            ) : (
              <Skeleton style={{ height: 150 }} />
            )}
          </section>

          <section className={styles.block} aria-label="Connections">
            <div className={styles.blockHead}>
              <h3 className={styles.blockTitle}>Connections</h3>
              <Button size="sm" icon={<PlugZap size={14} />} loading={vendors.isPending} onClick={() => vendors.mutate()}>
                Test connections
              </Button>
            </div>
            <p className={styles.sub}>
              A live test call with each key from the server&apos;s .env, a request to the knowledge source, and a
              sign-in to Jira when tickets go there. A revoked or expired key shows up here.
            </p>
            {vendors.isError ? <Alert tone="danger">{errorMessage(vendors.error)}</Alert> : null}
            {vendors.data ? (
              <ul className={styles.checks} role="list" aria-label="Connections">
                {CONNECTIONS.map(({ key, label, icon }) => (
                  <CheckRow key={key} icon={icon} label={label} check={vendors.data[key]} />
                ))}
              </ul>
            ) : null}
          </section>
        </div>
      ) : null}
    </dialog>
  );
}
